import React, {useEffect, useRef, useState} from 'react';
import {Alert, Button, Form, Input, InputNumber, message, Modal, Radio, Select, Spin} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {useAtom, useAtomValue} from 'jotai';
import {artistOptionsAtom} from './ddex_atoms';
import {uploadedFilesAtom} from './FileUploadStep';
import axiosApiInstance from '../../../api/axiosClient';
import {artTrackPriceTierOptions, genreOptions, genreToSubgenreOptions, languageOptions} from "../../../common/ddex_options";
import TrackArtistSelect from './TrackArtistSelect';
import {GENAI_DISCLOSURE_OPTIONS, GENAI_DISCLOSURE_TOOLTIP, GENAI_FULLY_ALERT} from './genaiDisclosure';

interface TrackEditorModalProps {
    visible: boolean;
    track: API.MusicDistribution.ArtTrack | null;
    onClose: () => void;
    onSave: (track: API.MusicDistribution.ArtTrack) => void;
    isNew?: boolean;
    releaseId?: string;
    isFromOtherRelease?: boolean;
    releaseGenre?: string;
    releaseSubGenre?: string;
}

const TrackEditorModal: React.FC<TrackEditorModalProps> = ({
                                                               visible,
                                                               track,
                                                               onClose,
                                                               onSave,
                                                               isNew = false,
                                                               releaseId,
                                                               isFromOtherRelease = false,
                                                               releaseGenre,
                                                               releaseSubGenre,
                                                           }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const artistOptions = useAtomValue(artistOptionsAtom);
    const knownArtistKeys = React.useMemo(
        () => new Set(artistOptions.map((option) => String(option.value))),
        [artistOptions],
    );

    const existingArtistRule = {
        validator: async (_: unknown, value?: string[]) => {
            if (!value?.length) {
                return;
            }
            const unknown = value.filter((key) => !knownArtistKeys.has(key));
            if (unknown.length > 0) {
                throw new Error(`Select existing artists only: ${unknown.join(', ')}`);
            }
        },
    };
    const [uploadedFiles] = useAtom(uploadedFilesAtom);
    const [generatedISRC, setGeneratedISRC] = useState('');
    const generatedISRCRef = useRef('');

    const rememberGeneratedISRC = (code: string) => {
        generatedISRCRef.current = code;
        setGeneratedISRC(code);
    };

    const unbindGeneratedISRC = (code: string) => {
        if (!code) {
            return;
        }
        axiosApiInstance.put(`/api/products/unbind_isrc/${encodeURIComponent(code)}`).catch((error) => {
            console.error('Error unbinding ISRC:', error);
        });
        if (generatedISRCRef.current === code) {
            generatedISRCRef.current = '';
            setGeneratedISRC('');
        }
    };

    // Determine if fields should be disabled
    const isFieldDisabled = isFromOtherRelease;

    // Instrumental tracks have no authors/lyricists: disable (and clear) the
    // Authors field when "Is Instrumental" is set to Yes.
    const isInstrumental = Form.useWatch('is_instrumental', form) === true;
    const selectedGenre = Form.useWatch('genre', form);

    // Filter only audio files for the filename dropdown
    const audioFiles = uploadedFiles.filter(file => file.fileType === 'audio');

    useEffect(() => {
        console.log({releaseIdTE: releaseId})
    }, [releaseId]);

    // Helper function to find field-specific validation warnings
    const getFieldWarning = (fieldName: string) => {
        if (!track?.validation_warnings || track.validation_warnings.length === 0) {
            return null;
        }

        return track.validation_warnings.find(warning => warning.field === fieldName);
    };

    useEffect(() => {
        if (visible) {
            generatedISRCRef.current = '';
            setGeneratedISRC('');
            return;
        }
        const reserved = generatedISRCRef.current;
        if (reserved) {
            unbindGeneratedISRC(reserved);
        }
    }, [visible]);

    useEffect(() => {
        if (visible && track) {
            const trackGenre = track.genre || '';
            const trackSubGenre = track.sub_genre || '';
            form.setFieldsValue({
                ...track,
                main_artists: track.main_artists?.map(artist => artist.key_name),
                featuring_artists: track.featuring_artists?.map(artist => artist.key_name),
                composers: track.composers?.map(artist => artist.key_name),
                authors: track.authors?.map(artist => artist.key_name),
                remixers: track.remixers?.map(artist => artist.key_name),
                producers: track.producers?.map(artist => artist.key_name),
                arrangers: track.arrangers?.map(artist => artist.key_name),
                publishers: track.publishers?.map(artist => artist.key_name),
                recording_engineers: track.recording_engineers?.map(artist => artist.key_name),
                mixing_engineers: track.mixing_engineers?.map(artist => artist.key_name),
                mastering_engineers: track.mastering_engineers?.map(artist => artist.key_name),
                executive_producers: track.executive_producers?.map(artist => artist.key_name),
                // Blank track genre/subgenre inherit the release values in the form
                // so the dropdowns show what DDEX will actually send.
                genre: trackGenre || releaseGenre,
                sub_genre: trackGenre ? trackSubGenre : (trackSubGenre || releaseSubGenre),
            });
        }
    }, [visible, track, form, releaseGenre, releaseSubGenre]);

    const handleSave = async () => {
        try {
            const values = await form.getFieldsValue();
            setLoading(true);

            const genre = values.genre || '';
            const subGenre = values.sub_genre || '';
            const matchesRelease =
                (!genre || genre === (releaseGenre || '')) &&
                (!subGenre || subGenre === (releaseSubGenre || ''));

            // Transform values to match API expectations
            const transformedValues = {
                ...values,
                main_artists: values.main_artists?.map((key_name: string) => ({key_name})),
                featuring_artists: values.featuring_artists?.map((key_name: string) => ({key_name})),
                composers: values.composers?.map((key_name: string) => ({key_name})),
                authors: values.authors?.map((key_name: string) => ({key_name})),
                remixers: values.remixers?.map((key_name: string) => ({key_name})),
                producers: values.producers?.map((key_name: string) => ({key_name})),
                arrangers: values.arrangers?.map((key_name: string) => ({key_name})),
                publishers: values.publishers?.map((key_name: string) => ({key_name})),
                recording_engineers: values.recording_engineers?.map((key_name: string) => ({key_name})),
                mixing_engineers: values.mixing_engineers?.map((key_name: string) => ({key_name})),
                mastering_engineers: values.mastering_engineers?.map((key_name: string) => ({key_name})),
                executive_producers: values.executive_producers?.map((key_name: string) => ({key_name})),
                preview_start: Number(values.preview_start ?? 45),
                // Persist blank when the form still matches the release so DDEX
                // inherits live album genre; store overrides only when they differ.
                genre: matchesRelease ? '' : genre,
                sub_genre: matchesRelease ? '' : subGenre,
            };

            if (!transformedValues.filename) {
                transformedValues.filename = '';
                transformedValues.audio_file_release_id = ''; // Clear audio file release ID if no file is selected
            }

            // Save to backend
            const endpoint = isNew ? '/api/products/art_tracks/' : '/api/products/art_tracks/';
            const method = isNew ? 'post' : 'put';

            const response = await axiosApiInstance[method](endpoint, transformedValues);

            // Get the created/updated track from the response
            const savedTrack = response.data.track || transformedValues;

            // If this is a new track and we have a releaseId, handle placement
            if (isNew && releaseId && savedTrack.id) {
                try {
                    // Make a separate API call to update the placement
                    await axiosApiInstance.put('/api/products/art_tracks/placement', {
                        track_id: savedTrack.id,
                        release_id: releaseId,
                        track_isrc: savedTrack.isrc,
                        volume_number: values.volume_number,
                        track_number: values.track_number
                    });

                    message.success('Track placement updated successfully');
                } catch (placementError) {
                    console.error('Error updating track placement:', placementError);
                    message.warning('Track created but placement could not be updated');
                }
            }

            message.success(`Track ${isNew ? 'created' : 'updated'} successfully`);
            generatedISRCRef.current = '';
            setGeneratedISRC('');
            // Use the track returned from the backend to ensure we have the correct UUID
            onSave(savedTrack);
            // onClose();
        } catch (error) {
            console.error('Error saving track:', error);

            // Check if the error is related to a track with audio from another release
            if (error.response && error.response.status === 403 &&
                error.response.data && error.response.data.error &&
                error.response.data.error.includes('Cannot update metadata for a track with audio from another release')) {
                message.error('Cannot update metadata for a track with audio from another release');
            } else {
                message.error('Failed to save track');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateISRC = async () => {
        if (generatedISRC) {
            form.setFieldValue('isrc', generatedISRC);
            return;
        }
        try {
            const response = await axiosApiInstance.get('/api/products/new_isrc');
            const data = response.data;
            const isrc = typeof data === 'string' ? data : data?.isrc;
            if (!isrc) {
                message.error('Failed to generate ISRC');
                return;
            }
            rememberGeneratedISRC(isrc);
            form.setFieldValue('isrc', isrc);
        } catch (error) {
            console.error('Error generating ISRC:', error);
            message.error('Failed to generate ISRC');
        }
    };

    const handleClose = () => {
        const reserved = generatedISRCRef.current;
        if (reserved) {
            unbindGeneratedISRC(reserved);
        }
        onClose();
    };

    return (
        <Modal
            title={isNew ? "Create New Track" : "Edit Track"}
            open={visible}
            width={800}
            onCancel={handleClose}
            footer={[
                <Button key="cancel" onClick={handleClose}>
                    Cancel
                </Button>,
                <Button
                    key="save"
                    type="primary"
                    onClick={handleSave}
                    loading={loading}
                    disabled={isFieldDisabled}
                >
                    Save
                </Button>
            ]}
        >
            <Spin spinning={loading}>
                {isFromOtherRelease && (
                    <Alert
                        message="This track's audio file is from another release"
                        description="The metadata of this track cannot be edited because its audio file is stored in another release's folder."
                        type="warning"
                        showIcon
                        style={{marginBottom: 16}}
                    />
                )}

                {track?.validation_warnings && track.validation_warnings.length > 0 && (
                    <Alert
                        message={`Track has ${track.validation_warnings.length} validation warning${track.validation_warnings.length !== 1 ? 's' : ''}`}
                        description={
                            <ul style={{margin: '8px 0 0 0', padding: '0 0 0 20px'}}>
                                {track.validation_warnings.map((warning, index) => (
                                    <li key={index}>{warning.message}</li>
                                ))}
                            </ul>
                        }
                        type="warning"
                        showIcon
                        style={{marginBottom: 16}}
                    />
                )}
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{track_type: 'ORIGINAL'}}
                    style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}
                >
                    <div style={{gridColumn: '1 / -1'}}>
                        <Form.Item name="id" hidden>
                            <Input/>
                        </Form.Item>

                        <Form.Item name="volume_number" hidden>
                            <Input/>
                        </Form.Item>

                        <Form.Item name="track_number" hidden>
                            <Input/>
                        </Form.Item>

                        <Form.Item name="audio_file_release_id" hidden>
                            <Input/>
                        </Form.Item>
                    </div>

                    <Form.Item
                        label="ISRC"
                        name="isrc"
                        rules={[{ /*required: true,*/ message: 'Please input the ISRC'}]}
                        validateStatus={getFieldWarning('isrc') ? 'warning' : undefined}
                        help={getFieldWarning('isrc')?.message}
                    >
                        <Input
                            placeholder="Leave blank to generate automatically"
                            onChange={(e) => {
                                const value = e.target.value;
                                if (generatedISRC && value !== generatedISRC) {
                                    unbindGeneratedISRC(generatedISRC);
                                }
                            }}
                            suffix={
                                <Button type="link" onClick={handleGenerateISRC} disabled={isFieldDisabled}>
                                    Generate
                                </Button>
                            }
                            disabled={isFieldDisabled}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Title"
                        name="title"
                        rules={[{message: 'Please input the title', warningOnly: true}]}
                        validateStatus={getFieldWarning('title') ? 'warning' : undefined}
                        help={getFieldWarning('title')?.message}
                    >
                        <Input disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Subtitle"
                        name="subtitle"
                        validateStatus={getFieldWarning('subtitle') ? 'warning' : undefined}
                        help={getFieldWarning('subtitle')?.message}
                    >
                        <Input disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Track Type"
                        name="track_type"
                    >
                        <Select
                            options={[
                                {label: 'Original', value: 'ORIGINAL'},
                                {label: 'Karaoke', value: 'KARAOKE'},
                                {label: 'Medley', value: 'MEDLEY'},
                                {label: 'Cover', value: 'COVER'},
                                {label: 'Cover by Band', value: 'COVER BY BAND'},
                            ]}
                            disabled={isFieldDisabled}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Main Artists"
                        name="main_artists"
                        validateStatus={getFieldWarning('main_artists') ? 'warning' : undefined}
                        help={getFieldWarning('main_artists')?.message}
                        rules={[
                            {message: 'Please select at least one main artist', warningOnly: true},
                            existingArtistRule,
                        ]}
                    >
                        <TrackArtistSelect disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Featuring Artists"
                        name="featuring_artists"
                        validateStatus={getFieldWarning('featuring_artists') ? 'warning' : undefined}
                        help={getFieldWarning('featuring_artists')?.message}
                        rules={[existingArtistRule]}
                    >
                        <TrackArtistSelect disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Composers"
                        name="composers"
                        validateStatus={getFieldWarning('composers') ? 'warning' : undefined}
                        help={getFieldWarning('composers')?.message}
                        rules={[{message: 'Please select at least one composer', warningOnly: true}]}
                    >
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Authors"
                        name="authors"
                        validateStatus={getFieldWarning('authors') ? 'warning' : undefined}
                        help={isInstrumental ? 'Not applicable for instrumental tracks' : getFieldWarning('authors')?.message}
                    >
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled || isInstrumental}/>
                    </Form.Item>

                    <Form.Item
                        label="Remixers"
                        name="remixers"
                    >
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Producers"
                        name="producers"
                    >
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Arrangers"
                        name="arrangers"
                    >
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Publishers"
                        name="publishers"
                    >
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item label="Recording engineers" name="recording_engineers">
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item label="Mixing engineers" name="mixing_engineers">
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item label="Mastering engineers" name="mastering_engineers">
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item label="Executive producers" name="executive_producers">
                        <TrackArtistSelect allowCustom disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Filename"
                        name="filename"
                        style={{gridColumn: '1 / -1'}}
                        validateStatus={getFieldWarning('filename') ? 'warning' : undefined}
                        help={getFieldWarning('filename')?.message}
                        rules={[{message: 'Please select an audio file', warningOnly: true}]}
                    >
                        <Select
                            showSearch
                            placeholder="Select an audio file"
                            optionFilterProp="children"
                            options={audioFiles.map(file => ({
                                label: file.name,
                                value: file.name
                            }))}
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={isFromOtherRelease}
                            allowClear
                        />
                    </Form.Item>

                    {/* Add missing fields that are validated in the backend */}
                    <Form.Item
                        label="Title Language"
                        name="title_language"
                        validateStatus={getFieldWarning('title_language') ? 'warning' : undefined}
                        help={getFieldWarning('title_language')?.message}
                    >
                        <Select
                            placeholder="Select title language"
                            allowClear
                            options={languageOptions}
                            disabled={isFieldDisabled}
                            showSearch
                        />
                    </Form.Item>

                    <Form.Item
                        label="Lyrics Language"
                        name="lyrics_language"
                        validateStatus={getFieldWarning('lyrics_language') ? 'warning' : undefined}
                        help={getFieldWarning('lyrics_language')?.message}
                    >
                        <Select
                            allowClear
                            placeholder="Select lyrics language"
                            options={languageOptions}
                            disabled={isFieldDisabled}
                            showSearch
                        />
                    </Form.Item>

                    <Form.Item
                        label="Parental Advisory"
                        name="parental_advisory"
                        validateStatus={getFieldWarning('parental_advisory') ? 'warning' : undefined}
                        help={getFieldWarning('parental_advisory')?.message}
                        initialValue={'No'}
                    >
                        <Radio.Group
                            options={[
                                {label: 'No', value: 'No'},
                                {label: 'Yes', value: 'Yes'},
                            ]}
                            disabled={isFieldDisabled}
                        />
                    </Form.Item>

                    <Form.Item
                        label="GenAI disclosure"
                        name="contains_ai"
                        tooltip={{title: GENAI_DISCLOSURE_TOOLTIP, icon: <InfoCircleOutlined/>}}
                        validateStatus={getFieldWarning('contains_ai') ? 'warning' : undefined}
                        help={getFieldWarning('contains_ai')?.message}
                    >
                        <Radio.Group
                            options={GENAI_DISCLOSURE_OPTIONS}
                            disabled={isFieldDisabled}
                        />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(prev, cur) => prev.contains_ai !== cur.contains_ai}>
                        {({getFieldValue}) =>
                            getFieldValue('contains_ai') === 'All' ? (
                                <Alert
                                    type="warning"
                                    showIcon
                                    style={{marginBottom: 16}}
                                    message={GENAI_FULLY_ALERT}
                                />
                            ) : null
                        }
                    </Form.Item>

                    <Form.Item
                        label="Is Instrumental"
                        name="is_instrumental"
                        initialValue={false}
                    >
                        <Radio.Group
                            options={[
                                {label: 'No', value: false},
                                {label: 'Yes', value: true},
                            ]}
                            disabled={isFieldDisabled}
                            onChange={(e) => {
                                if (e.target.value === true) {
                                    form.setFieldValue('authors', []);
                                }
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Preview Start (sec)"
                        name="preview_start"
                        initialValue={45}
                        tooltip="Seconds from the start of the track (YouTube preview / Shorts clip start)"
                    >
                        <InputNumber min={0} style={{width: '100%'}} addonAfter="sec" disabled={isFieldDisabled}/>
                    </Form.Item>

                    <Form.Item
                        label="Price"
                        name="track_price_tier"
                    >
                        <Select
                            defaultValue="SINGLE2"
                            disabled={isFieldDisabled}
                            options={artTrackPriceTierOptions}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Genre"
                        name="genre"
                        extra={
                            releaseGenre
                                ? `Leave blank to use the release genre (${releaseGenre}${releaseSubGenre ? ` / ${releaseSubGenre}` : ''})`
                                : 'Leave blank to use the release genre'
                        }
                    >
                        <Select
                            allowClear
                            showSearch
                            placeholder={releaseGenre || 'Release genre'}
                            options={genreOptions.map((g) => ({label: g, value: g}))}
                            disabled={isFieldDisabled}
                            onChange={() => form.setFieldValue('sub_genre', undefined)}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Subgenre"
                        name="sub_genre"
                    >
                        <Select
                            allowClear
                            showSearch
                            placeholder={
                                selectedGenre
                                    ? 'Select subgenre'
                                    : (releaseSubGenre || 'Release subgenre')
                            }
                            options={((selectedGenre && (genreToSubgenreOptions as Record<string, string[]>)[selectedGenre]) || []).map((s: string) => ({
                                label: s,
                                value: s,
                            }))}
                            disabled={isFieldDisabled || !selectedGenre}
                        />
                    </Form.Item>

                    {isFromOtherRelease && track?.audio_file_release_id && (
                        <Form.Item
                            label="Audio Source"
                            style={{gridColumn: '1 / -1'}}
                        >
                            <Input
                                value={`Release ID: ${track.audio_file_release_id}`}
                                disabled
                                addonBefore="Audio file is stored in"
                            />
                        </Form.Item>
                    )}
                </Form>
            </Spin>
        </Modal>
    );
};

export default TrackEditorModal;
